<template>
    <div>
        <loader :loading="isLoading" v-if="isLoading"></loader>
        <transition name="fade">
            <div v-if="isItemsDirty" class="message">
                <div>Number of added items: {{ addedItems.length }}</div>
                <div>Number of removed items: {{ removedItems.length }}</div>
                <div>
                    <ClientColorButton @click="saveItems" :disabled="isLoading">Save</ClientColorButton>&nbsp;
                    <ClientColorButton @click="revertChanges" :disabled="isLoading">Cancel</ClientColorButton>
                </div>
            </div>
        </transition>
        <!-- Have this extra wrapping div to stop item search from disappearing -->
        <div class="forecast-overview">
            <div class="row mb-4">
                <div class="col-12 col-xl-4 col-md-6">
                    <p class="forecast-overview-title mb-3 text-uppercase">
                        {{ items.length }} Item{{ items.length > 1 ? "s" : "" }} included in the forecast
                    </p>

                    <div v-if="items.length > 0">
                        <div class="row">
                            <div class="col-12">
                                <label for="forecast-overview-input-filter" class="mb-2">Search for an item in the forecast :</label>
                                <div class="w-100"><input id="forecast-overview-input-filter" type="text" v-model="search" /></div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12 col-xl-8 col-md-6 d-flex flex-fill" v-if="canUpdated()">
                    <div class="row m-0 forecast-overview-add-section w-100">
                        <div class="col-12 col-xl">
                            <ItemSearch
                                :forecast="forecast"
                                :forecast-windows="forecastWindows"
                                :excluded-items="modifiedItems"
                                :resetSearch="resetSearchBool"
                                @resetSearch="resetSearch"
                                @item-added="addItem"
                            />
                        </div>
                        <div class="col-12 col-xl-auto mt-3 mt-xl-0 d-flex align-items-end justify-content-center">
                            <ForcastButton class="mx-5" @click.stop="$router.push(`/catalog`);" :size="'l'">Add items from catalogue</ForcastButton>
                        </div>
                        <div class="col-12 col-xl-auto mt-3 mt-xl-0 d-flex align-items-end justify-content-center">
                            <ForecastXlsImport
                                :forecast="forecast"
                                :key="componentForecastXlsImportKey"
                                @reload="reload"
                                @reloadItems="reloadItems();"
                            ></ForecastXlsImport>
                        </div>
                    </div>
                </div>
                <div class="col-12 mb-sm-3" v-if="canUpdated()">
                    <div class="d-flex justify-content-start align-items-center flex-wrap selectedbuttons">
                        <ForcastButton
                            class="mr-3 mt-3"
                            :disabled="itemSelection.length === modifiedItems.length"
                            :size="'m'"
                            @click="itemSelection = modifiedItems;"
                        >
                            Select All
                        </ForcastButton>
                        <ForcastButton class="mr-3 mt-3" :disabled="itemSelection.length === 0" :size="'m'" @click="itemSelection = [];">
                            Select None
                        </ForcastButton>
                        <div class="sorting d-flex justify-content-start align-items-center">
                            <p>Sort by :</p>
                            <ForcastButton class="mr-3 mt-3" :size="'m'" @click="sortItems('reference');"> Reference </ForcastButton>
                            <ForcastButton class="mr-3 mt-3" :size="'m'" v-if="hasItemsColor" @click="sortItems('color');"> Color </ForcastButton>
                        </div>
                        <ItemColors
                            v-if="itemSelection.length > 0"
                            :items="itemSelection"
                            :forecast="forecast"
                            @colorsChanged="colorChange"
                        ></ItemColors>
                        <ForcastButton
                            class="mt-3"
                            v-if="itemSelection.length > 0 && modifiedItems.length > 0 && hasAccessByProfile(PROFILES.FORECAST_MANAGER)"
                            @click="removeSelection"
                            :size="'m'"
                            style="margin-left: auto"
                        >
                            Remove selected items
                        </ForcastButton>
                    </div>
                </div>
            </div>
            <div v-if="items.length > 0">
                <ForecastTable>
                    <template slot="head-row-2">
                        <th></th>
                        <th></th>
                        <th></th>
                        <th class="text-center">Item</th>
                        <th class="text-center">Description</th>
                        <th class="text-center">Total QTY</th>
                        <th class="text-center">Markets with prices</th>
                        <th></th>
                    </template>
                    <template slot="body">
                        <draggable
                            tag="tbody"
                            :list="items"
                            :multi-drag="true"
                            selected-class="selected-class"
                            :group="{ name: 'people', pull: 'clone', put: true }"
                            animation="200"
                            ghost-class="ghost"
                            handle=".handle"
                            @end="dragEnd"
                        >
                            <tr
                                v-for="(item, index) in modifiedItems"
                                v-show="!item.hide"
                                :key="item.id"
                                class="selected-class"
                                :class="{ isDisabled: isItemDisabled(item) }"
                            >
                                <td
                                    class="align-middle text-center handle"
                                    :style="item.color != null && item.color !== '' ? 'border-left: 15px solid ' + item.color : ''"
                                >
                                    <img style="ccursor: grabbing;position:relative;" src="../../../../../static/img/drag-and-drop.svg" />
                                </td>
                                <td class="align-middle text-center"><input type="checkbox" v-model="itemSelection" :value="item" /></td>
                                <td class="align-middle text-center">{{ getOrder(item, index) }}</td>
                                <td class="align-middle">
                                    <ItemImage class="item-image" :item="item" :height="'100px'" :width="'100px'" align="center" />
                                </td>
                                <td class="align-middle"><ItemDescription :item="item" :forecast="forecast" /></td>
                                <td class="align-middle text-center">{{ getQuantities(item) }}</td>
                                <td class="align-middle"><AllPricesInfo :item="item" :forecast-windows="forecastWindows" /></td>
                                <td class="align-middle" style="text-align: right; padding-right: 20px">
                                    <ForcastButton
                                        :size="'m'"
                                        v-if="hasAccessByProfile(PROFILES.FORECAST_MANAGER)"
                                        :disabled="!canRemoveItem(item)"
                                        @click="removeItems([item]);"
                                        >Remove</ForcastButton
                                    >
                                    <ItemQuantitiesButton style="margin-left: 20px" :item="item" :forecast="forecast" />
                                </td>
                            </tr>
                        </draggable>
                    </template>
                </ForecastTable>
            </div>
            <div v-else><p class="text-center no-items">No item in this forecast yet</p></div>
        </div>
    </div>
</template>
<script>
import ForecastValidator from "@/components/forecasts/validation/forecastValidator";
import ItemImage from "@/components/forecasts/elements/ItemImage";
import ForecastTable from "@/components/forecasts/elements/table/ForecastTable";
import ItemDescription from "@/components/forecasts/elements/ItemDescription";
import ItemSearch from "@/components/forecasts/pages/sections/itemSearch/ItemSearch";
import loader from "@/components/loader/loader";
import AllPricesInfo from "@/components/forecasts/elements/AllPricesInfo";
import { mapActions } from "vuex";
import { FORECAST_STATUS } from "@/components/forecasts/constants";
import forecastsService from "@/components/forecasts/forecastsService";
import ItemValidator from "@/components/forecasts/validation/itemValidator";
import { PROFILES } from "@/plugins/AccessUserPlugin";
import ItemQuantitiesButton from "@/components/forecasts/elements/table/ItemQuantitiesButton";
import { getQuantitiesForForecastWindows } from "@/components/forecasts/utils/utilis";
import DefaultButton from "@/components/forecasts/elements/form/DefaultButton";
import ForcastButton from "@/components/forecasts/elements/form/ForcastButton";
import draggable from "vuedraggable";
import ForecastXlsImport from "@/components/forecasts/elements/ForecastXlsImport";
import historyService from "@/services/history";
import { OBJECT_TYPE, SHORT_DESCRIPTION } from "@/components/history/constants";
import ItemColors from "@/components/forecasts/pages/sections/itemsColor/ItemColors";

export default {
    components: {
        ForecastXlsImport,
        draggable,
        ForcastButton,
        DefaultButton,
        ForecastTable,
        ItemSearch,
        ItemDescription,
        ItemImage,
        AllPricesInfo,
        loader,
        ItemQuantitiesButton,
        ItemColors
    },
    props: {
        forecast: {
            type: Object,
            required: true
        },
        forecastWindows: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            search: "",
            itemSelection: [],
            addedItems: [],
            removedItems: [],
            isLoading: true,
            resetSearchBool: false,
            componentForecastXlsImportKey: 0,
            items: [],
            PROFILES,
            history: {
                objectType: OBJECT_TYPE.FORECASTS,
                objectId: this.forecast.id
            },
            itemWindowsProperties: []
        };
    },
    async created() {
        forecastsService.getItems(this.forecast.id, true).then(items => {
            if (this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) {
                forecastsService.getItemsFwProperties(this.forecast.id).then(itemsProperties => {
                    this.itemWindowsProperties = itemsProperties;

                    if (this.itemWindowsProperties.length > 0) {
                        for (let itemPropertie of this.itemWindowsProperties) {
                            let item = items.find(item => item.id === itemPropertie.item.id);
                            if (itemPropertie.color != null && itemPropertie.color !== "") {
                                item.color = itemPropertie.color;
                            }
                            if (itemPropertie.order !== null && itemPropertie.color !== 0) {
                                item.order = itemPropertie.order;
                            }
                        }

                        items.sort((a, b) => {
                            return parseInt(a.order) - parseInt(b.order);
                        });
                    }
                });
            }
            this.items = items;
        });

        this.isLoading = false;
    },
    computed: {
        needsExplicitSave() {
            return this.forecast.status !== FORECAST_STATUS.DRAFT;
        },
        isRemoveSelectionDisabled() {
            return (
                this.itemSelection.length === 0 ||
                (this.forecast.status !== FORECAST_STATUS.DRAFT && this.itemSelection.length === this.modifiedItems.length) ||
                this.removedItems.length > 0
            );
        },
        isItemsDirty() {
            return this.addedItems.length > 0 || this.removedItems.length > 0;
        },
        hasItemsColor() {
            return this.items.filter(item => item.color != null && item.color !== "").length > 0;
        },
        modifiedItems() {
            if (!this.needsExplicitSave) {
                return this.items.map(item => {
                    item.hide = !(
                        this.search === "" ||
                        item.clientSapRef.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                        item.clientEanRef.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                        item.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1
                    );
                    return item;
                });
            }
            if (!this.forecast) {
                return [];
            }
            return this.items
                .filter(item => {
                    return !this.removedItems.find(removedItem => removedItem.id === item.id);
                })
                .filter(item => {
                    return (
                        this.search === "" ||
                        item.clientSapRef.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                        item.clientEanRef.toLowerCase().indexOf(this.search.toLowerCase()) > -1 ||
                        item.name.toLowerCase().indexOf(this.search.toLowerCase()) > -1
                    );
                })
                .concat(
                    this.addedItems.filter(item => {
                        return !this.items.find(i => i.id === item.id);
                    })
                );
        }
    },
    methods: {
        canUpdated() {
            return (
                (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER) || this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) &&
                !this.forecast.isTransmissionApproved
            );
        },
        colorChange: async function(color) {
            for (let items of this.itemSelection) {
                items.color = color;
            }
            this.items.sort((a, b) => {
                return this.itemSelection.indexOf(b) - this.itemSelection.indexOf(a);
            });
            if (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER)) {
                await forecastsService.updateItemsOrderMasse(this.forecast, this.items);
            } else if (this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) {
                await forecastsService.updateItemsFwProperties(this.forecast, this.items);
            }
        },
        async dragEnd(e) {
            const item = this.modifiedItems.find(i => i.order === e.newIndex + 1);
            if (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER)) {
                await forecastsService.updateItemsOrder(this.forecast, { item: item.id, oldOrder: e.oldIndex + 1, newOrder: e.newIndex + 1 });
            } else {
                await forecastsService.updateItemsFwProperties(this.forecast, this.items);
            }
        },
        async reload() {
            this.componentForecastXlsImportKey++;
        },
        async reloadItems() {
            this.isLoading = true;
            this.items = await forecastsService.getItems(this.forecast.id, true);
            // this.forecast.numberOfItems = this.items.length;
            this.isLoading = false;
        },
        ...mapActions("forecast", { updateItems: "updateItems" }),
        canRemoveItem(item) {
            let copiedForecast = { ...this.forecast };
            copiedForecast.items = this.modifiedItems;
            return ForecastValidator.canRemoveItem(copiedForecast, item);
        },
        isItemDisabled(item) {
            return ItemValidator.isDisabled(item);
        },
        revertChanges() {
            this.addedItems = [];
            this.removedItems = [];
        },
        removeSelection() {
            this.removeItems(this.itemSelection);
            this.itemSelection = [];
        },
        saveItems() {
            this.isLoading = true;
            this.updateItems({ forecast: this.forecast, itemsToRemove: this.removedItems, itemsToAdd: this.addedItems })
                .then(() => {
                    if (this.addedItems.length > 0) {
                        this.items = this.items.concat(this.addedItems);
                        this.addedItems = [];
                    }

                    if (this.removedItems.length > 0) {
                        this.items = this.items.filter(item => !this.removedItems.some(rItem => rItem.id === item.id));
                        this.removedItems = [];
                    }
                    this.dialogSuccess("Forecast has been updated");
                    this.isLoading = false;
                })
                .catch(() => {
                    this.loading = false;
                    this.$swal({
                        text: "ERROR TO ADD / REMOVE ITEM",
                        type: "error",
                        confirmButtonText: "OK",
                        confirmButtonColor: "rgb(135, 135, 135)",
                        allowOutsideClick: false,
                        allowEscapeKey: false
                    });
                });
        },
        addItem(item, speedAdd) {
            if (this.needsExplicitSave) {
                this.addedItems.push(item);
                this.removedItems = this.removedItems.filter(i => i.id !== item.id);
            } else {
                this.isLoading = true;
                this.updateItems({ forecast: this.forecast, itemsToAdd: [item] })
                    .then(() => {
                        if (!this.items.some(fullItem => fullItem.id === item.id)) {
                            this.items.push(item);
                        }
                        if (speedAdd) {
                            this.resetSearchBool = true;
                        }
                        this.isLoading = false;
                        this.dialogSuccess(SHORT_DESCRIPTION.ADD_ITEM);
                    })
                    .catch(() => {
                        this.loading = false;
                        this.$swal({
                            text: "ERROR TO ADD ITEM",
                            type: "error",
                            confirmButtonText: "OK",
                            confirmButtonColor: "rgb(135, 135, 135)",
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        });
                    });
            }
        },
        resetSearch(value) {
            this.resetSearchBool = value;
        },
        async removeItems(itemsToRemove) {
            if (this.needsExplicitSave) {
                itemsToRemove.forEach(itemToRemove => {
                    // remove item from list of added items
                    if (this.addedItems.some(i => i.id === itemToRemove.id)) {
                        this.addedItems = this.addedItems.filter(i => i.id !== itemToRemove.id);
                    } else {
                        // add item to list of removed items
                        if (!this.removedItems.some(i => i.id === itemToRemove.id)) {
                            this.removedItems.push(itemToRemove);
                        }
                    }
                    // remove item from item selection list
                    this.itemSelection = this.itemSelection.filter(i => i.id !== itemsToRemove.id);
                });
            } else {
                let items = this.items;
                this.items = [];
                this.items = items.filter(i => !itemsToRemove.some(item => item.id === i.id));
                this.isLoading = true;
                await this.updateItems({ forecast: this.forecast, itemsToRemove: itemsToRemove })
                    .then(() => {
                        this.isLoading = false;
                        this.dialogSuccess(SHORT_DESCRIPTION.REMOVE_ITEM);
                    })
                    .catch(() => {
                        this.loading = false;
                        this.$swal({
                            text: "ERROR TO REMOVE ITEM",
                            type: "error",
                            confirmButtonText: "OK",
                            confirmButtonColor: "rgb(135, 135, 135)",
                            allowOutsideClick: false,
                            allowEscapeKey: false
                        });
                    });
            }
        },
        getQuantities(item) {
            return getQuantitiesForForecastWindows(item.id, this.forecastWindows);
        },
        getOrder(item, index) {
            item.order = index + 1;
            return item.order;
        },
        sortItems(by) {
            if (by === "reference") {
                this.items.sort((a, b) => {
                    return parseInt(a.clientEanRef) - parseInt(b.clientEanRef);
                });
            } else if (by === "color") {
                this.items.sort((a, b) => {
                    const colorA = a.color != null ? a.color : "";
                    const colorB = b.color != null ? b.color : "";
                    return ("" + colorB).localeCompare(colorA);
                });
            }

            if (this.hasAccessByProfile(PROFILES.FORECAST_MANAGER)) {
                forecastsService.updateItemsOrderMasse(this.forecast, this.items);
            } else if (this.hasAccessByProfile(PROFILES.ZONES_SUPERVISOR)) {
                forecastsService.updateItemsFwProperties(this.forecast, this.items);
            }
        }
    }
};
</script>
<style scoped lang="scss">
@import "../../css/forecasts";
.items-included-text {
    text-transform: uppercase;
    font-size: 1.2em;
    text-align: left;
    .count {
        padding-right: 5px;
        color: $color7;
        font-size: 1.1em;
    }
}
.message {
    position: fixed;
    width: 110%;
    bottom: 0;
    background-color: #ddd;
    padding: 15px 150px;
    margin-left: -130px;
    text-align: left;
    z-index: 100;
    div {
        padding: 5px 0;
    }
}
.fetching-items {
    padding: 30px;
    font-weight: bold;
    text-transform: uppercase;
}
tr.isDisabled {
    background-color: $color_background_disabled;
}
.search {
    margin-top: 20px;
    margin-bottom: 40px;
}
.forecast-overview {
    background-color: #fff;
    padding: 20px;

    .forecast-overview-title {
        font-family: Oswald, Sans-Serif;
        font-size: 26px;
    }

    #forecast-overview-input-filter {
        width: 230px !important;
    }

    .forecast-overview-add-section {
        background-color: #f4f4f4;
        padding: 20px;
    }
}
input {
    border: 1px solid;
    border-radius: 3px;
    font-size: 14px;
    padding: 4px 12px;
    color: #a2a2a2;
}

.no-items {
    font-size: 18px;
}

.selectedbuttons {
    margin: 15px 0 -5px;
    min-height: 65px;
    p {
        margin: 0.5em 10px 0 0;
        @media (min-width: 1416px) {
            margin: 0.5em 10px 0 10px;
        }
    }

    @media (max-width: 522px) {
        button:last-child {
            margin-left: 0 !important;
        }
    }
}
</style>



// WEBPACK FOOTER //
// ItemManager.vue?706c13af